import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ConfirmResponse,
  DuplicateResponse,
  IntakeDetail,
  IntakeListResponse,
  IntakeStats,
  Stage,
  TransitionRequestBody,
  TransitionResponse,
  UploadResponse,
} from './types'

// Sprint 22: kept as alias so the IntakeListFilters interface below
// retains its old name without forcing every consumer to rename.
type IntakeStatus = Stage

const INTAKE_KEY = ['sales-inbox'] as const

export interface IntakeListFilters {
  status?: IntakeStatus
  customer_id?: number
  date_from?: string
  date_to?: string
  /** Show archived (Odoo-confirmed/cancelled) rows instead of the active board. */
  archived?: boolean
  /** Show the Pending-Odoo lane: cards linked to an Odoo SO but not yet
   *  confirmed (off the active board). Mutually exclusive with archived. */
  pending_odoo?: boolean
  limit?: number
  offset?: number
}

/**
 * List intake records. Backend filters by sales-rep customer scope
 * automatically when the requesting user has scope_partner_ids set
 * (Sprint 16 pattern).
 */
export function useIntakeList(filters: IntakeListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.customer_id != null) params.set('customer_id', String(filters.customer_id))
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  if (filters.archived) params.set('archived', 'true')
  if (filters.pending_odoo) params.set('pending_odoo', 'true')
  params.set('limit', String(filters.limit ?? 50))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...INTAKE_KEY, 'list', filters],
    queryFn: () =>
      jsonFetch<IntakeListResponse>(`/api/sales-intake?${qs}`),
    staleTime: 15_000,
    refetchInterval: (query) => {
      // Adaptive refetch: faster when extraction is in flight (pending
      // or running) — Sprint 22 moved the parsing sub-state onto its
      // own `extraction_state` column, orthogonal to the commercial
      // `status` (= Stage).
      const data = query.state.data as IntakeListResponse | undefined
      if (!data) return 60_000
      const inFlight = data.items.some(
        (i) => i.extraction_state === 'pending' || i.extraction_state === 'running',
      )
      return inFlight ? 5_000 : 60_000
    },
  })
}

export function useIntakeStats() {
  return useQuery({
    queryKey: [...INTAKE_KEY, 'stats'],
    queryFn: () => jsonFetch<IntakeStats>('/api/sales-intake/stats'),
    staleTime: 30_000,
  })
}

export function useIntakeDetail(intakeId: string | null) {
  return useQuery({
    queryKey: [...INTAKE_KEY, 'detail', intakeId],
    queryFn: () =>
      jsonFetch<IntakeDetail>(`/api/sales-intake/${intakeId}`),
    enabled: !!intakeId,
    staleTime: 5_000,
  })
}

export function useUploadIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return jsonFetch<UploadResponse>('/api/sales-intake/upload', {
        method: 'POST',
        rawBody: fd,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useConfirmIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (intakeId: string) =>
      jsonFetch<ConfirmResponse>(`/api/sales-intake/${intakeId}/confirm`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useRejectIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { intakeId: string; reason: string }) =>
      jsonFetch<{ intake_id: string; status: string }>(
        `/api/sales-intake/${vars.intakeId}/reject`,
        {
          method: 'POST',
          body: { reason: vars.reason },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useDismissWarnings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (intakeId: string) =>
      jsonFetch<{ intake_id: string; warnings_dismissed_at: string }>(
        `/api/sales-intake/${intakeId}/dismiss-warnings`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useAcknowledgeIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (intakeId: string) =>
      jsonFetch<{ intake_id: string; acknowledged_at: string | null; acknowledged_by: string | null }>(
        `/api/sales-intake/${intakeId}/acknowledge`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useRetryExtract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (intakeId: string) =>
      jsonFetch<{ intake_id: string; status: string }>(
        `/api/sales-intake/${intakeId}/extract`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

/**
 * Sprint 22 Chunk 5 — operator-driven commercial pipeline transition.
 * Validation against ALLOWED_TRANSITIONS happens server-side; client
 * surfaces 400 errors as toast/error UI.
 */
export function useTransitionIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { intakeId: string; targetStage: Stage; note?: string }) => {
      const body: TransitionRequestBody = {
        target_stage: vars.targetStage,
      }
      if (vars.note) body.note = vars.note
      return jsonFetch<TransitionResponse>(
        `/api/sales-intake/${vars.intakeId}/transition`,
        { method: 'POST', body },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTAKE_KEY })
    },
  })
}

export function useCheckDuplicate() {
  return useMutation({
    mutationFn: (intakeId: string) =>
      jsonFetch<DuplicateResponse>(
        `/api/sales-intake/${intakeId}/check-duplicate`,
        { method: 'POST' },
      ),
  })
}

export function useLinkOdooSo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { intakeId: string; odoo_so_id: number; odoo_so_name: string }) =>
      jsonFetch<{ intake_id: string; odoo_so_id: number; odoo_so_name: string; odoo_so_url: string }>(
        `/api/sales-intake/${vars.intakeId}/link-odoo-so`,
        { method: 'POST', body: { odoo_so_id: vars.odoo_so_id, odoo_so_name: vars.odoo_so_name } },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['intake-detail', vars.intakeId] })
    },
  })
}

/**
 * Send a customer reply email via MailDesk (mirrors /regulatory + /support).
 * Threads into the source conversation and records the outbound back into the
 * email-thread viewer; invalidating the cache refreshes the thread + history.
 */
export function useSendIntakeResponse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      id: string
      target_email: string
      subject: string
      body: string
    }) =>
      jsonFetch<{ sent: boolean; to: string }>(
        `/api/sales-intake/${vars.id}/send-response`,
        {
          method: 'POST',
          body: {
            target_email: vars.target_email,
            subject: vars.subject,
            body: vars.body,
          },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: INTAKE_KEY }),
  })
}

/**
 * Per-user CARLA conversation thread for an intake. Returns the
 * conversation_id (creating one if it doesn't exist yet).
 */
export interface IntakeConversationResponse {
  conversation_id: string
  created: boolean
}

export function useIntakeConversation(intakeId: string | null) {
  return useQuery({
    queryKey: [...INTAKE_KEY, 'conversation', intakeId],
    queryFn: () =>
      jsonFetch<IntakeConversationResponse>(
        `/api/sales-intake/${intakeId}/conversation`,
      ),
    enabled: !!intakeId,
    staleTime: 5 * 60_000,
  })
}
